import { ShieldAlert, X, Heart } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { onAdBlocked, isAdBlocked } from './AdSenseBanner';

const DISMISS_KEY = 'weather-story-adblock-dismissed';
const DISMISS_DAYS = 14;

function isDismissed(): boolean {
  const dismissed = localStorage.getItem(DISMISS_KEY);
  if (!dismissed) return false;
  const expiresAt = parseInt(dismissed, 10);
  if (Date.now() > expiresAt) {
    localStorage.removeItem(DISMISS_KEY);
    return false;
  }
  return true;
}

interface AdBlockDetectorProps {
  className?: string;
}

export function AdBlockDetector({ className = '' }: AdBlockDetectorProps) {
  const [detected, setDetected] = useState(isAdBlocked);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Listen for ad-block detection from AdSenseBanner instances
    const unsub = onAdBlocked(() => setDetected(true));
    // Also check if already detected
    if (isAdBlocked()) setDetected(true);
    return unsub;
  }, []);

  useEffect(() => {
    if (detected && !isDismissed()) {
      // Small delay so it doesn't flash immediately on load
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, [detected]);

  if (!visible) return null;

  const handleDismiss = () => {
    const expiresAt = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, expiresAt.toString());
    setVisible(false);
  };

  return (
    <div
      className={`relative bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-700/30 rounded-2xl p-5 ${className}`}
    >
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-gray-500 hover:text-gray-300 transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <ShieldAlert className="h-5 w-5 text-amber-400" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-100 mb-1 flex items-center gap-1.5">
            We get it — ads can be annoying
          </h4>
          <p className="text-xs text-gray-400">
            Ads help keep Weathr Story free for everyone. If you'd prefer an ad-free experience,
            consider supporting us with a subscription — you'll also get extra features!
          </p>
        </div>

        <Link
          to="/subscription"
          className="shrink-0 inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Heart className="h-3.5 w-3.5" />
          Go Ad-Free
        </Link>
      </div>
    </div>
  );
}
